// The Call: shared vocabulary and row types (docs/features/the-call.md)
//
// A "call sport" is the unit a user follows: the conditions-page sport
// vocabulary collapsed to what a Call is about. Piste and off-piste collapse
// to "ski" (one mountain, one Call per great day); everything else maps 1:1.

import {
  isSupProfileKey,
  sportPresentation,
} from "@/lib/conditions/sport-profiles";
import type { FactorConfidence } from "@/lib/conditions/factor-explanations";
import type { ConditionSafetyState } from "@/lib/conditions/safety-notes";

export const CALL_SPORTS = [
  "ski",
  "touring",
  "mtb",
  "surf",
  "bodyboarding",
  "wind",
  "foil",
  "downwind-foil",
  "wing-foil",
  "sup",
  "sup-touring",
  "sup-surf",
  "sup-downwind",
  "swim",
  "diving",
  "sailing",
  "sailing-club",
  "sailing-hp",
] as const;

export type CallSport = (typeof CALL_SPORTS)[number];

const CALL_SPORT_SET = new Set<string>(CALL_SPORTS);

export function isCallSport(value: string): value is CallSport {
  return CALL_SPORT_SET.has(value);
}

/** Resolve a stored preference into the exact Call sport used for candidate
 * construction. The legacy SUP alias only maps in this direction. */
export function normaliseCallSportPreference(value: string): CallSport | null {
  if (value === "sup") return "sup-touring";
  return isCallSport(value) ? value : null;
}

/** Canonical public projection of stored Call follows. Disabled and
 * write-dark retain generic SUP and hide exact profiles. Verified public
 * access maps the legacy alias one way to Touring and deduplicates it. */
export function canonicalCallSportsForAccess(
  values: readonly string[],
  includeNamedSupProfiles: boolean,
): CallSport[] {
  return [...new Set(
    projectStoredCallSportsForAccess(values, includeNamedSupProfiles)
      .filter(isCallSport),
  )];
}

/** Raw state-API projection. Legacy responses preserve every unrelated
 * stored string and its order, while hiding exact dark profiles. Verified
 * public responses canonicalise only the SUP alias and deduplicate it. */
export function projectStoredCallSportsForAccess(
  values: readonly string[],
  includeNamedSupProfiles: boolean,
): string[] {
  if (!includeNamedSupProfiles) {
    return values.filter((value) => !isSupProfileKey(value));
  }
  return [...new Set(values.map((value) => value === "sup"
    ? "sup-touring"
    : value))];
}

export const CALL_SPORT_LABELS: Record<CallSport, string> = {
  ski: "Ski",
  touring: "Ski touring",
  mtb: "Mountain biking",
  surf: "Surf",
  bodyboarding: "Bodyboarding",
  wind: "Windsurfing",
  foil: "Prone foiling",
  "downwind-foil": "Downwind foiling",
  "wing-foil": "Wing foiling",
  sup: "SUP",
  "sup-touring": `${sportPresentation("sup-touring").label} (Beta)`,
  "sup-surf": `${sportPresentation("sup-surf").label} (Beta)`,
  "sup-downwind": `${sportPresentation("sup-downwind").label} (Beta)`,
  swim: "Open water swimming",
  diving: "Diving",
  sailing: "Sailing",
  "sailing-club": "Club sailing",
  "sailing-hp": "High-performance sailing",
};

/**
 * Map a conditions-page URL sport key (?sport=) to the call sport the
 * follow bell should act on. Returns the page-category default when the
 * param is absent or unknown.
 */
export function callSportFromUrlKey(
  urlKey: string | null,
  category: "ski" | "summer" | "sailing"
): CallSport {
  const fallback: CallSport =
    category === "ski" ? "ski" : category === "sailing" ? "sailing-club" : "surf";
  if (!urlKey) return fallback;
  const mapped: Record<string, CallSport> = {
    piste: "ski",
    "off-piste": "ski",
    touring: "touring",
    mtb: "mtb",
    surf: "surf",
    bodyboarding: "bodyboarding",
    wind: "wind",
    foil: "foil",
    "downwind-foil": "downwind-foil",
    "wing-foil": "wing-foil",
    sup: "sup",
    "sup-touring": "sup-touring",
    "sup-surf": "sup-surf",
    "sup-downwind": "sup-downwind",
    swimming: "swim",
    swim: "swim",
    diving: "diving",
    sailing: "sailing-club",
    "sailing-club": "sailing-club",
    "sailing-hp": "sailing-hp",
  };
  return mapped[urlKey] ?? fallback;
}

/** The ?sport= URL key a conditions page expects for a call sport. Ski has
 * no param (mountain pages default to piste). */
export function urlKeyFromCallSport(sport: CallSport): string | null {
  if (sport === "ski") return null;
  if (sport === "swim") return "swimming";
  if (sport === "sailing") return "sailing-club";
  return sport;
}

// ---------------------------------------------------------------------------
// Row types
// ---------------------------------------------------------------------------

export type CallStatus =
  | "watching"
  | "fired"
  | "upgraded"
  | "collapsed"
  | "expired"
  | "verified";

/** Headline numbers shown on the card; per-sport subset is populated. */
export type CallPeak = {
  freshSnowCm?: number;
  snowDepthCm?: number;
  tempC?: number;
  swellM?: number;
  periodS?: number;
  windKmh?: number;
  gustKmh?: number;
  waterTempC?: number;
  safetyNote?: string;
  scoreConfidence?: FactorConfidence;
  scoreConfidenceReasons?: string[];
  safetyState?: ConditionSafetyState;
  safetyNotes?: string[];
  /**
   * The day's evidence class (A, B or C; C means model only), written only
   * while CONDITIONS_NON_SKI_EVIDENCE_POLICY is on, so the card can disclose it.
   */
  evidenceClass?: "A" | "B" | "C";
};

export type CallRarity = {
  /** Percentile of this score in the location's per-sport history (0-100). */
  percentile: number | null;
  /** "top5" | "top10" | null when history was too thin to judge. */
  tier: "top5" | "top10" | null;
  /** Number of scored history days behind the percentile. */
  sampleDays: number;
};

export type CallOutcome = {
  actualScore: number | null;
  verdict: "nailed" | "close" | "missed" | "no-data";
  stampedAt: string;
};

export type CallRow = {
  id: string;
  fingerprint: string;
  location_id: string;
  location_slug: string;
  location_name: string;
  country: string | null;
  location_timezone: string;
  sport: CallSport;
  rule_key: string;
  target_date: string;
  status: CallStatus;
  score: number;
  headline: string;
  window_copy: string | null;
  peak: CallPeak;
  rarity: CallRarity | null;
  confidence: number;
  lead_days: number;
  seen_runs: number;
  first_fired_at: string | null;
  update_count: number;
  outcome: CallOutcome | null;
  created_at: string;
  updated_at: string;
};

export type CallDeliveryChannel = "inapp" | "email" | "push";

export type CallDeliveryRow = {
  id: string;
  call_id: string;
  user_id: string;
  channel: CallDeliveryChannel;
  sent_at: string;
  read_at: string | null;
};
